import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, HelpCircle, MessageCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CursorGlow from '../components/CursorGlow';
import ScrollProgress from '../components/ScrollProgress';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'Tümü' },
    { id: 'general', name: 'Genel' },
    { id: 'services', name: 'Hizmetler' },
    { id: 'pricing', name: 'Fiyatlandırma' },
    { id: 'technical', name: 'Teknik' },
    { id: 'support', name: 'Destek' }
  ];

  const faqs = [
    {
      category: 'general',
      question: 'RUYAN Bilişim ne tür hizmetler sunuyor?',
      answer: 'Mobil uygulama geliştirme, yapay zeka entegrasyonu, web tasarımı, SEO, sosyal medya yönetimi ve e-ticaret çözümleri sunuyoruz.'
    },
    {
      category: 'general',
      question: 'Hangi sektörlere hizmet veriyorsunuz?',
      answer: 'E-ticaret, fintech, sağlık, eğitim, gayrimenkul ve daha birçok sektöre özel çözümler sunuyoruz.'
    },
    {
      category: 'services',
      question: 'Mobil uygulama geliştirme süreci ne kadar sürer?',
      answer: 'Proje karmaşıklığına bağlı olarak 8-16 hafta arasında değişir. MVP için 8-12 hafta, tam özellikli uygulamalar için 12-16 hafta sürer.'
    },
    {
      category: 'services',
      question: 'Hem iOS hem Android için uygulama geliştiriyor musunuz?',
      answer: 'Evet, React Native ile cross-platform veya Swift/Kotlin ile native geliştirme seçenekleri sunuyoruz.'
    },
    {
      category: 'services',
      question: 'Yapay zeka entegrasyonu nedir?',
      answer: 'ChatGPT, özel AI modelleri ve makine öğrenmesi ile ürünlerinize akıllı özellikler ekliyoruz.'
    },
    {
      category: 'pricing',
      question: 'Fiyatlandırma nasıl çalışıyor?',
      answer: 'Proje bazlı ve paket bazlı fiyatlandırma seçeneklerimiz var. Detaylar için fiyatlandırma sayfamızı ziyaret edin.'
    },
    {
      category: 'pricing',
      question: 'Ödeme planları var mı?',
      answer: 'Evet, taksitli ödeme ve milestone bazlı ödeme seçenekleri sunuyoruz.'
    },
    {
      category: 'pricing',
      question: 'Ücretsiz danışmanlık sunuyor musunuz?',
      answer: 'Evet, tüm potansiyel müşterilerimize ücretsiz ilk danışmanlık toplantısı sunuyoruz.'
    },
    {
      category: 'technical',
      question: 'Hangi teknolojileri kullanıyorsunuz?',
      answer: 'React, React Native, Node.js, Python, AWS, MongoDB, PostgreSQL ve modern teknoloji stack kullanıyoruz.'
    },
    {
      category: 'technical',
      question: 'Hosting ve domain hizmeti veriyor musunuz?',
      answer: 'Evet, AWS, Google Cloud veya Azure üzerinde hosting ve domain yönetimi hizmeti sunuyoruz.'
    },
    {
      category: 'technical',
      question: 'Güvenlik önlemleriniz nelerdir?',
      answer: 'SSL, HTTPS, veri şifreleme, güvenli API\'ler ve düzenli güvenlik testleri uyguluyoruz.'
    },
    {
      category: 'support',
      question: 'Proje teslimi sonrası destek sunuyor musunuz?',
      answer: 'Evet, tüm projelerimizde 1-12 ay arası ücretsiz destek paketi sunuyoruz.'
    },
    {
      category: 'support',
      question: 'Acil destek hizmeti var mı?',
      answer: 'Premium paketlerimizde 7/24 acil destek hizmeti mevcuttur.'
    },
    {
      category: 'support',
      question: 'Proje iptali mümkün mü?',
      answer: 'Proje başlamadan önce iptal edebilirsiniz. Başladıktan sonra tamamlanan aşamalar için ücret alınır.'
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-cyan-500/20 rounded-full blur-3xl top-0 right-1/4 animate-pulse"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="w-8 h-8 text-white" />
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Sıkça Sorulan
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Sorular</span>
          </h1>

          <p className="text-xl text-gray-400 mb-8">
            Merak ettiklerinizin cevaplarını burada bulabilirsiniz
          </p>

          {/* Search */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Soru ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-14 bg-slate-800/40 border-slate-700/50 text-white rounded-xl"
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                  activeCategory === category.id
                    ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white'
                    : 'bg-slate-800/40 text-gray-400 hover:text-white border border-slate-700/50'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredFaqs.length > 0 ? (
            <Accordion type="single" collapsible className="space-y-4">
              {filteredFaqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl px-6 data-[state=open]:border-cyan-500/50"
                >
                  <AccordionTrigger className="text-white hover:text-cyan-400 text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">Aradığınız soruyu bulamadık.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MessageCircle className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
          <h2 className="text-4xl font-bold text-white mb-6">
            Cevabınızı Bulamadınız mı?
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Ekibimiz size yardımcı olmaktan mutluluk duyar
          </p>
          <Link to="/iletisim">
            <Button className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-8 py-6 text-lg">
              Bize Ulaşın
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;
